import React, { useState, useEffect } from 'react';
import { ShieldAlert, AlertTriangle, Check, X } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { useToast } from '../../../hooks/use-toast';
import BackButton from '../../../components/admin/BackButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BLACKLIST_REASONS = [
  'Fraud',
  'Abuse',
  'Non-payment',
  'High risk',
  'Low reputation score',
  'Suspicious activity',
  'Manual flag'
];

const Blacklist = () => {
  const [entityType, setEntityType] = useState('client');
  const [entityId, setEntityId] = useState('');
  const [entities, setEntities] = useState([]);
  const [blacklistStatus, setBlacklistStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({ reason: '', notes: '' });
  const { toast } = useToast();

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    fetchEntities();
  }, [entityType]);

  const fetchEntities = async () => {
    try {
      let endpoint = '';
      switch (entityType) {
        case 'client':
          endpoint = '/api/crm/clients';
          break;
        case 'lead':
          endpoint = '/api/crm/leads';
          break;
        case 'contact':
          endpoint = '/api/crm/contacts';
          break;
        case 'opportunity':
          endpoint = '/api/crm/opportunities';
          break;
        default:
          return;
      }

      const response = await fetch(`${API_URL}${endpoint}?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch entities');

      const data = await response.json();
      setEntities(data);

      if (data.length > 0 && !entityId) {
        setEntityId(data[0].id);
        fetchBlacklistStatus(entityType, data[0].id);
      }
    } catch (error) {
      console.error('Error fetching entities:', error);
      toast({
        title: 'Error',
        description: 'Failed to load entities',
        variant: 'destructive'
      });
    }
  };

  const fetchBlacklistStatus = async (type, id) => {
    if (!id) return;

    try {
      setLoading(true);

      const response = await fetch(
        `${API_URL}/api/crm/blacklist/${type}/${id}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) {
        // Entity might not have blacklist data yet
        setBlacklistStatus({
          status: 'not_blacklisted',
          reason: null,
          notes: null,
          evidence: [],
          history: []
        });
        return;
      }

      const data = await response.json();
      setBlacklistStatus(data);
    } catch (error) {
      console.error('Error fetching blacklist:', error);
      setBlacklistStatus({
        status: 'not_blacklisted',
        reason: null,
        notes: null,
        evidence: [],
        history: []
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddToBlacklist = async () => {
    if (!formData.reason) {
      toast({
        title: 'Validation Error',
        description: 'Please select a reason',
        variant: 'destructive'
      });
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/api/crm/blacklist/${entityType}/${entityId}/add`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        }
      );

      if (!response.ok) throw new Error('Failed to add to blacklist');

      toast({
        title: 'Success',
        description: 'Entity added to blacklist'
      });

      setShowAddForm(false);
      setFormData({ reason: '', notes: '' });
      fetchBlacklistStatus(entityType, entityId);
    } catch (error) {
      console.error('Error adding to blacklist:', error);
      toast({
        title: 'Error',
        description: 'Failed to add to blacklist',
        variant: 'destructive'
      });
    }
  };

  const handleRemoveFromBlacklist = async () => {
    if (!window.confirm('Are you sure you want to remove this entity from blacklist?')) return;

    try {
      const response = await fetch(
        `${API_URL}/api/crm/blacklist/${entityType}/${entityId}/remove`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ notes: 'Removed via UI' })
        }
      );

      if (!response.ok) throw new Error('Failed to remove from blacklist');

      toast({
        title: 'Success',
        description: 'Entity removed from blacklist'
      });

      fetchBlacklistStatus(entityType, entityId);
    } catch (error) {
      console.error('Error removing from blacklist:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove from blacklist',
        variant: 'destructive'
      });
    }
  };

  const handleSetUnderReview = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/crm/blacklist/${entityType}/${entityId}/review`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ notes: 'Marked for review via UI' })
        }
      );

      if (!response.ok) throw new Error('Failed to set under review');

      toast({
        title: 'Success',
        description: 'Entity marked as under review'
      });

      fetchBlacklistStatus(entityType, entityId);
    } catch (error) {
      console.error('Error setting under review:', error);
      toast({
        title: 'Error',
        description: 'Failed to set under review',
        variant: 'destructive'
      });
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'blacklisted':
        return (
          <span className="px-4 py-2 rounded-full bg-red-100 text-red-800 font-semibold flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5" />
            <span>Blacklisted</span>
          </span>
        );
      case 'under_review':
        return (
          <span className="px-4 py-2 rounded-full bg-yellow-100 text-yellow-800 font-semibold flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5" />
            <span>Under Review</span>
          </span>
        );
      default:
        return (
          <span className="px-4 py-2 rounded-full bg-green-100 text-green-800 font-semibold flex items-center space-x-2">
            <Check className="h-5 w-5" />
            <span>Not Blacklisted</span>
          </span>
        );
    }
  };

  const getEntityLabel = (entity) => {
    if (entityType === 'client' || entityType === 'lead') {
      return entity.company_name || entity.name || 'Unknown';
    } else if (entityType === 'contact') {
      return entity.name || 'Unknown';
    } else if (entityType === 'opportunity') {
      return entity.name || 'Unknown';
    }
    return 'Unknown';
  };

  return (
    <div>
      <BackButton />

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-red-100 rounded-lg">
            <ShieldAlert className="h-6 w-6 text-red-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-[#2B2D42]">Blacklist Management</h2>
            <p className="text-sm text-[#6C757D]">Manage blacklisted entities and risk compliance</p>
          </div>
        </div>
      </div>

      {/* Entity Selection */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Entity Type
              </label>
              <select
                value={entityType}
                onChange={(e) => {
                  setEntityType(e.target.value);
                  setEntityId('');
                  setBlacklistStatus(null);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
              >
                <option value="client">Client</option>
                <option value="lead">Lead</option>
                <option value="contact">Contact</option>
                <option value="opportunity">Opportunity</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select {entityType.charAt(0).toUpperCase() + entityType.slice(1)}
              </label>
              <select
                value={entityId}
                onChange={(e) => {
                  setEntityId(e.target.value);
                  fetchBlacklistStatus(entityType, e.target.value);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
              >
                <option value="">Select {entityType}...</option>
                {entities.map((entity) => (
                  <option key={entity.id} value={entity.id}>
                    {getEntityLabel(entity)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Blacklist Status */}
      {loading ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-[#6C757D]">Loading blacklist status...</p>
          </CardContent>
        </Card>
      ) : blacklistStatus ? (
        <div className="space-y-6">
          {/* Status Card */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-[#2B2D42]">Status</h3>
                {getStatusBadge(blacklistStatus.status)}
              </div>

              {blacklistStatus.reason && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-1">Reason</p>
                  <p className="text-base text-[#2B2D42]">{blacklistStatus.reason}</p>
                </div>
              )}

              {blacklistStatus.notes && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-1">Notes</p>
                  <p className="text-base text-[#6C757D]">{blacklistStatus.notes}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-3 mt-6 pt-6 border-t border-gray-200">
                {blacklistStatus.status === 'not_blacklisted' && (
                  <Button
                    onClick={() => setShowAddForm(!showAddForm)}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    <ShieldAlert className="h-4 w-4 mr-2" />
                    Add to Blacklist
                  </Button>
                )}

                {blacklistStatus.status === 'blacklisted' && (
                  <>
                    <Button
                      onClick={handleRemoveFromBlacklist}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Check className="h-4 w-4 mr-2" />
                      Remove from Blacklist
                    </Button>
                    <Button
                      onClick={handleSetUnderReview}
                      variant="outline"
                    >
                      Mark for Review
                    </Button>
                  </>
                )}

                {blacklistStatus.status === 'under_review' && (
                  <>
                    <Button
                      onClick={handleRemoveFromBlacklist}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Check className="h-4 w-4 mr-2" />
                      Remove from Blacklist
                    </Button>
                    <Button
                      onClick={() => setShowAddForm(true)}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      Confirm Blacklist
                    </Button>
                  </>
                )}
              </div>

              {/* Add Form */}
              {showAddForm && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-base font-semibold text-[#2B2D42] mb-4">Add to Blacklist</h4>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Reason <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={formData.reason}
                        onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
                      >
                        <option value="">Select reason...</option>
                        {BLACKLIST_REASONS.map((reason) => (
                          <option key={reason} value={reason}>{reason}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Notes
                      </label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] resize-none"
                        placeholder="Additional details..."
                      />
                    </div>

                    <div className="flex space-x-3">
                      <Button
                        onClick={handleAddToBlacklist}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        Confirm Add
                      </Button>
                      <Button
                        onClick={() => {
                          setShowAddForm(false);
                          setFormData({ reason: '', notes: '' });
                        }}
                        variant="outline"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* History */}
          {blacklistStatus.history && blacklistStatus.history.length > 0 && (
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold text-[#2B2D42] mb-4">History</h3>
                <div className="space-y-3">
                  {blacklistStatus.history.map((entry, index) => (
                    <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-[#2B2D42]">{entry.details}</p>
                        <p className="text-xs text-[#6C757D] mt-1">
                          {new Date(entry.timestamp).toLocaleString()} • {entry.user_id}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      ) : (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-[#6C757D]">
              Select an entity to view blacklist status
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Blacklist;
